import { instagramContract } from '@daymade/api-contracts';
import { ClientInferResponseBody } from '@ts-rest/core';
import ImageWithFallback from 'components/ImageWithFallback';
import { dashboardFolder } from 'constants/Dashboard';
import { ReactElement, useEffect, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { instagramApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import styles from './InstagramFeed.module.css';

type TInstagramPost = ClientInferResponseBody<
  typeof instagramContract.public.getLatestPosts,
  200
>['latestPosts'][0];

interface Props {
  wrapper?: (x: ReactElement) => ReactElement;
}

export const InstagramFeed = (props: Props) => {
  const [posts, setPosts] = useState<TInstagramPost[] | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await tryApi(
          instagramApiClient.public.getLatestPosts({
            query: { limit: 8 },
          }),
        );

        if (response.status === HttpStatusCode.OK) {
          const data = response.body.latestPosts;
          setPosts(data);
        }
      } catch (error) {
        console.error('Error fetching Instagram posts:', error);
      }
    })();
  }, []);

  if (!posts || posts.length === 0) {
    return null;
  }

  const componentInnerOutput = (
    <div className={styles.container}>
      <div className={styles.postGrid}>
        {posts.map((post) => (
          <a
            key={post.id}
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.postLink}
          >
            {post.media_type && post.media_type === 'VIDEO' && (
              <span className={styles.playButton}>
                <ImageWithFallback
                  src={`${dashboardFolder}/play-button.svg`}
                  alt="Play"
                  width={16}
                  height={16}
                  className={styles.playButtonIcon}
                />
              </span>
            )}
            <ImageWithFallback
              src={post.thumbnail_url || post.media_url}
              alt={post.caption || 'Instagram post'}
              layout="fill"
              objectFit="cover"
              className={styles.postImage}
            />
          </a>
        ))}
      </div>
    </div>
  );

  if (props.wrapper) {
    return props.wrapper(componentInnerOutput);
  }
  return componentInnerOutput;
};
